import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const API_BASE_URL = 'https://dolphin-app-33jp4.ondigitalocean.app';

function SkillMaster() {
  const navigate = useNavigate();
  const [hasRefAccess, setHasRefAccess] = useState(false);

  // ── Mirror Navbar refAccess logic ─────────────────────────────────────────
  useEffect(() => {
    const checkRefAccess = () => {
      try {
        const userData = localStorage.getItem('userData');
        const user = userData ? JSON.parse(userData) : null;
        if (user && (user.role === 'super_admin' || user.email === 'info@easylearn.net.in')) {
          setHasRefAccess(true);
        } else {
          setHasRefAccess(localStorage.getItem('refAccess') === '1');
        }
      } catch {
        setHasRefAccess(false);
      }
    };

    checkRefAccess();

    const pollRefAccess = async () => {
      try {
        const userData = localStorage.getItem('userData');
        if (!userData) return;
        const user = JSON.parse(userData);
        if (!user?.email || user.role === 'super_admin' || user.email === 'info@easylearn.net.in') return;
        const res = await fetch(
          `${API_BASE_URL}/isfathena/user/can-access-reference?email=${encodeURIComponent(user.email)}`
        );
        const data = await res.json();
        const newVal = data.can_access ? '1' : '0';
        if (newVal !== localStorage.getItem('refAccess')) {
          localStorage.setItem('refAccess', newVal);
          setHasRefAccess(data.can_access);
        }
      } catch {}
    };

    const handleStorageEvent = (e) => {
      if (e.key === 'refAccessToggle') {
        try {
          const { email, value } = JSON.parse(e.newValue);
          const userData = localStorage.getItem('userData');
          if (userData) {
            const user = JSON.parse(userData);
            if (user.email === email) {
              localStorage.setItem('refAccess', value);
              setHasRefAccess(value === '1');
            }
          }
        } catch {}
      }
      checkRefAccess();
    };

    const pollInterval = setInterval(pollRefAccess, 10000);
    window.addEventListener('storage', handleStorageEvent);
    window.addEventListener('refAccessChange', checkRefAccess);

    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('storage', handleStorageEvent);
      window.removeEventListener('refAccessChange', checkRefAccess);
    };
  }, []);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // Disable right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable certain keyboard shortcuts
    const handleKeyDown = (e) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J')) ||
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Disable text selection and copy
    const handleCopy = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable drag
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('dragstart', handleDragStart);

    // Disable text selection via CSS
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('dragstart', handleDragStart);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.msUserSelect = '';
    };
  }, []);

  return (
    <div className="bg-black text-white min-h-screen overflow-x-hidden w-full">
      {/* Navbar */}
      <Navbar />

      {/* Protection styles for images */}
      <style jsx>{`
        img {
          pointer-events: none;
          user-select: none;
          -webkit-user-drag: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
        html, body {
          overflow-x: hidden;
          max-width: 100%;
        }
      `}</style>

      {/* Background stars effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {[...Array(80)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full opacity-50"
            style={{
              width: Math.random() * 2 + 1 + 'px',
              height: Math.random() * 2 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 sm:py-32 overflow-hidden min-h-screen flex items-center">
        {/* Background Image using img tag */}
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src="/Assests/home_page_37.png" 
            alt="Skill Master Background" 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Dark overlay - Reduced opacity */}
        <div className="absolute inset-0 bg-black/30"></div>

        <div className="max-w-7xl mx-auto relative z-10 w-full">

          {/* Main Heading */}
          <h1 className="text-2xl sm:text-4xl lg:text-6xl font-bold text-white mb-6 sm:mb-8 leading-tight max-w-6xl">
            Skill Master Upskilling Software
          </h1>

          {/* Subheading */}
          <h2 className="text-xl sm:text-3xl lg:text-4xl font-bold text-white mb-8 sm:mb-12 leading-tight max-w-5xl">
            Accelerate Your Personal and Organizational Excellence
          </h2>

          {/* Description */}
          <p className="text-gray-300 text-lg lg:text-xl leading-relaxed max-w-4xl mb-12">
            Transform your career and organization with cutting-edge upskilling solutions designed for the modern workforce. Skill Master combines three decades of expertise with innovative technology to deliver measurable results that drive success.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-3 sm:gap-4">
            <button className="px-6 sm:px-8 py-3 bg-orange-500 text-white rounded-md font-medium hover:bg-orange-600 transition">
              Start Your Journey
            </button>
            <button
              onClick={() => navigate('/reffrence?productId=4')}
              className="px-8 py-3 border border-white text-white rounded-md font-medium hover:bg-white hover:text-orange-500 hover:border-orange-500 transition">
              Learn More
            </button>
          </div>

        </div>
      </section>

          {/* Chapter 1: Unlocking Excellence Section */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-32 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 overflow-hidden">
            <img 
            src="/Assests/home_page_11.png" 
            alt="Chapter 1 Background" 
            className="w-full h-full object-cover"
            />
        </div>
        
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/50"></div>

        <div className="max-w-7xl mx-auto relative z-10">
            
            {/* Main Heading */}
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8 leading-tight">
            Chapter 1: Unlocking Excellence for People and Businesses
            </h2>

            {/* First Paragraph */}
            <p className="text-gray-300 text-lg lg:text-xl leading-relaxed mb-6">
            In today's rapidly evolving marketplace, the gap between current skills and future needs continues to widen. Organizations and individuals who fail to adapt risk being left behind. Skill Master bridges this critical divide with proven methodologies and cutting-edge technology.
            </p>

            {/* Second Paragraph */}
            <p className="text-gray-300 text-lg lg:text-xl leading-relaxed">
            Our comprehensive approach combines strategic thinking, personalized coaching, and technology-driven learning to create transformational experiences that deliver lasting results for both individuals and organizations.
            </p>

        </div>
    </section>

        {/* The Skill Master Promise Section */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-20 ">
      <div className="max-w-7xl mx-auto">
        
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          
          {/* Left Side - Image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden">
              <img 
                src="/Assests/home_page_38.png" 
                alt="Skill Master Promise" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Right Side - Content */}
          <div className="space-y-8">
            
            {/* Main Heading */}
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8">
              The Skill Master Promise
            </h2>

            {/* Cards Grid */}
            <div className="grid gap-6">
              
              {/* First Row - Two Cards */}
              <div className="grid md:grid-cols-2 gap-6">
                
                {/* 30+ Years Expertise Card */}
                <div className="border-2 border-orange-500 rounded-2xl p-6 bg-black/50">
                  <h3 className="text-xl font-bold text-white mb-4">
                    30+ Years Expertise
                  </h3>
                  <p className="text-gray-300 text-base leading-relaxed">
                    Over three decades of combined experience in technology innovation and human excellence development, creating proven frameworks for success.
                  </p>
                </div>

                {/* Individual & Organizational Empowerment Card */}
                <div className="border-2 border-orange-500 rounded-2xl p-6 bg-black/50">
                  <h3 className="text-xl font-bold text-white mb-4">
                    Individual & Organizational Empowerment
                  </h3>
                  <p className="text-gray-300 text-base leading-relaxed">
                    Designed to unlock high performance potential across all levels, from individual contributors to executive leadership teams.
                  </p>
                </div>

              </div>

              {/* Second Row - Full Width Card */}
              <div className="border-2 border-orange-500 rounded-2xl p-6 bg-black/50">
                <h3 className="text-xl font-bold text-white mb-4">
                  Unique Integrated Approach
                </h3>
                <p className="text-gray-300 text-base leading-relaxed">
                  Seamless blend of strategic planning, personalized coaching, and technology-driven learning creates comprehensive growth experiences.
                </p>
              </div>

            </div>

          </div>

        </div>

      </div>
    </section>

        {/* Learn Anytime Section */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-orange-400">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* First Card - Learn Anytime with reduced opacity */}
        <div className="bg-black/60 rounded-3xl p-6 sm:p-12">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Learn Anytime, Anywhere, At Your Own Pace
          </h2>
          <p className="text-gray-300 text-lg lg:text-xl leading-relaxed">
            Break free from traditional learning constraints with our flexible, technology-powered platform. Whether you're a busy executive or an emerging professional, access world-class training content when and where it works for you.
          </p>
        </div>

        {/* Second Card - Why Upskilling Matters */}
        <div className="bg-black/60 rounded-3xl p-6 sm:p-12">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8">
            Why Upskilling Matters 
          </h2>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            
            {/* Left Side - Chart */}
            <div className="bg-neutral-900 rounded-2xl p-4 sm:p-8">
              <div style={{ width: '100%', height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      { name: 'L&D Budget', fullName: 'L&D Budget Increase', value: 65 },
                      { name: 'Learning Demand', fullName: 'Employee Learning Demand', value: 75 },
                      { name: 'Skill Gap', fullName: 'Skill Gap Recognition', value: 90 }
                    ]}
                    margin={{ top: 30, right: 20, left: 10, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#404040" vertical={false} />
                    <XAxis
                      dataKey="name"
                      angle={-35}
                      textAnchor="end"
                      height={70}
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                      interval={0}
                      tickLine={false}
                    />
                    <YAxis
                      domain={[0, 100]}
                      tick={{ fill: '#9ca3af', fontSize: 11 }}
                      tickFormatter={(value) => `${value}%`}
                      axisLine={false}
                      tickLine={false}
                      width={40}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1f2937',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '13px'
                      }}
                      formatter={(value, name, props) => [`${value}%`, props.payload.fullName]}
                      cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                    />
                    <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={80}
                      label={{ position: 'top', formatter: (v) => `${v}%`, fill: '#fff', fontSize: 13, fontWeight: 'bold' }}
                    >
                      <Cell fill="#c2410c" />
                      <Cell fill="#ea580c" />
                      <Cell fill="#fed7aa" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Right Side - Text Content */}
            <div className="space-y-6">
              <p className="text-gray-300 text-lg leading-relaxed">
                The numbers tell a compelling story: organizations worldwide are recognizing the critical importance of continuous learning and development.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                Skill Master addresses these urgent needs with structured, measurable growth paths that bridge skill gaps and prepare teams for future challenges.
              </p>
            </div>

          </div>
        </div>

      </div>
    </section>

    {/* Chapter 2: Core Features Section */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-24 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 overflow-hidden">
        <img 
          src="/Assests/home_page_39.png" 
          alt="Chapter 2 Background" 
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/60"></div>

      <div className="max-w-6xl mx-auto relative z-10">
        
        {/* Content Card with border */}
        <div className="border-2 border-gray-500 rounded-3xl p-12 bg-black/40">
          
          {/* Main Heading */}
          <h2 className="text-4xl lg:text-5xl font-bold text-orange-500 mb-8 leading-tight">
            Core Features That Set Skill Master Apart
          </h2>

          {/* First Paragraph */}
          <p className="text-gray-300 text-lg lg:text-xl leading-relaxed mb-6">
            What makes Skill Master different isn't just our technology—it's our comprehensive understanding of how people learn and organizations grow. Our platform integrates multiple learning modalities with real-world application opportunities.
          </p>

          {/* Second Paragraph */}
          <p className="text-gray-300 text-lg lg:text-xl leading-relaxed">
            From individual skill development to organizational transformation, we provide tools and methodologies that create lasting change and measurable impact across all dimensions of professional growth.
          </p>

        </div>

      </div>
    </section>

    {/* Comprehensive Skill Development Ecosystem Section */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-20 ">
      <div className="max-w-7xl mx-auto">
        
        {/* Main Heading */}
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16 text-center">
          Comprehensive Skill Development Ecosystem
        </h2>

        {/* Three Column Grid */}
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-12">
          
          {/* Column 1 - Structured Capability Building */}
          <div className="text-center space-y-6">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16">
                <svg className="w-full h-full text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <rect x="3" y="3" width="7" height="7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <rect x="14" y="3" width="7" height="7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <rect x="14" y="14" width="7" height="7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <rect x="3" y="14" width="7" height="7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            
            {/* Title */}
            <h3 className="text-2xl font-bold text-gray-200">
              Structured Capability Building
            </h3>
            
            {/* Description */}
            <p className="text-gray-200 text-base leading-relaxed">
              Progressive learning paths across all management levels, ensuring systematic skill development that builds on foundational competencies and advances to expert-level mastery.
            </p>
          </div>

          {/* Column 2 - Leadership Development */}
          <div className="text-center space-y-6">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16">
                <svg className="w-full h-full text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="8" r="4" strokeWidth="2"/>
                  <path d="M12 12 L12 16 M12 16 L8 20 M12 16 L16 20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="4" r="1" fill="currentColor"/>
                </svg>
              </div>
            </div>
            
            {/* Title */}
            <h3 className="text-2xl font-bold text-gray-200">
              Leadership Development
            </h3>
            
            {/* Description */}
            <p className="text-gray-200 text-base leading-relaxed">
              Specialized programs for emerging and established leaders, focusing on future-fit leadership skills including emotional intelligence, strategic thinking, and change management.
            </p>
          </div>

          {/* Column 3 - NLP Certifications */}
          <div className="text-center space-y-6">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16">
                <svg className="w-full h-full text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="3" strokeWidth="2"/>
                  <path d="M12 3 L12 9 M12 15 L12 21 M3 12 L9 12 M15 12 L21 12" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M6 6 L9 9 M15 9 L18 6 M15 15 L18 18 M6 18 L9 15" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
            </div>
            
            {/* Title */}
            <h3 className="text-2xl font-bold text-gray-200">
              NLP Certifications
            </h3>
            
            {/* Description */}
            <p className="text-gray-200 text-base leading-relaxed">
              Comprehensive Neuro-Linguistic Programming certifications and behavioral workshops that enhance communication, influence, and personal effectiveness in professional contexts.
            </p>
          </div>

        </div>

      </div>
    </section>

    {/* Personalized Coaching & Therapy Section */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-orange-400/70">
      {/* Background stars effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(60)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-orange-200 rounded-full opacity-30 animate-pulse"
            style={{
              width: Math.random() * 3 + 1 + 'px',
              height: Math.random() * 3 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
              animationDelay: Math.random() * 2 + 's',
              animationDuration: Math.random() * 3 + 2 + 's'
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Main Heading */}
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16">
          Personalized Coaching & Therapy
        </h2>

        {/* Two Column Grid */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          
          {/* Left Side - Image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden">
              <img 
                src="/Assests/home_page_40.png" 
                alt="Personalized Coaching" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Right Side - Content */}
          <div className="space-y-8">
            
            {/* Subheading */}
            <h3 className="text-3xl font-bold text-black">
              Tailored Growth Support
            </h3>

            {/* First Paragraph */}
            <p className="text-black text-lg leading-relaxed">
              Our one-on-one coaching approach recognizes that every individual has unique strengths, challenges, and aspirations. Expert coaches work directly with learners to create personalized development plans.
            </p>

            {/* Second Paragraph */}
            <p className="text-black text-lg leading-relaxed">
              Integrated career guidance and self-excellence therapy components ensure that learning journeys address both professional skills and personal growth, creating holistic development experiences that drive lasting transformation.
            </p>

          </div>

        </div>

      </div>
    </section>
    {/* Strategy White Papers & Market-Ready Execution Section */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-32 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 overflow-hidden">
        <img 
          src="/Assests/home_page_41.png" 
          alt="Strategy Background" 
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/50"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Main Heading */}
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16">
          Strategy White Papers & Market-Ready Execution
        </h2>

        {/* Two Column Grid */}
        <div className="grid md:grid-cols-2 gap-6 sm:gap-12">
          
          {/* Left Card - Custom White Papers */}
          <div className="bg-black/60 border-l-4 border-orange-500 p-8">
            
            {/* Icon */}
            <div className="mb-6">
              <div className="w-14 h-14 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-2xl font-bold text-white mb-4">
              Custom White Papers
            </h3>

            {/* Description */}
            <p className="text-gray-300 text-base leading-relaxed">
              Data-driven research documents that validate go-to-market hypotheses for startups and established enterprises, providing actionable insights for strategic decision-making.
            </p>

          </div>

          {/* Right Card - GTM Acceleration */}
          <div className="bg-black/60 border-l-4 border-orange-500 p-8">
            
            {/* Icon */}
            <div className="mb-6">
              <div className="w-14 h-14 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-2xl font-bold text-white mb-4">
              GTM Acceleration
            </h3>

            {/* Description */}
            <p className="text-gray-300 text-base leading-relaxed">
              Proven go-to-market strategies backed by curated ecosystem support, helping organizations accelerate their market entry and expansion initiatives with confidence.
            </p>

          </div>

        </div>

      </div>
    </section>

    {/* Data-Driven Decisions for Business Growth Section */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-32 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 overflow-hidden">
        <img 
          src="/Assests/home_page_42.png" 
          alt="Data Driven Background" 
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/50"></div>

      <div className="max-w-6xl mx-auto relative z-10">
        
        {/* Content Card */}
        <div className="bg-black/70 rounded-3xl p-6 sm:p-12 lg:p-16 border border-gray-700">
          
          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-6xl font-bold text-white mb-6 sm:mb-8 leading-tight">
            Data-Driven Decisions for Business Growth
          </h2>

          {/* Description */}
          <p className="text-gray-300 text-lg lg:text-xl leading-relaxed">
            Transform complex market data into actionable strategies with our comprehensive white papers. Each document provides the insights and validation needed to make confident business decisions and accelerate market success.
          </p>

        </div>

      </div>
    </section>

    {/* Chapter 3: Real-World Impact & Client Success Stories Section */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-20">
      <div className="max-w-7xl mx-auto">
        
        {/* Two Column Grid */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          
          {/* Left Side - Text Content */}
          <div className="space-y-8">
            
            {/* Main Heading */}
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
              Chapter 3: Real-World Impact & Client Success Stories
            </h2>

            {/* First Paragraph */}
            <p className="text-gray-300 text-lg lg:text-xl leading-relaxed">
              The true measure of any upskilling platform lies in the real-world results it delivers. Skill Master's impact extends across industries and organization sizes, from innovative startups to established enterprises.
            </p>

            {/* Second Paragraph */}
            <p className="text-gray-300 text-lg lg:text-xl leading-relaxed">
              Our comprehensive approach has helped thousands of professionals and hundreds of organizations achieve breakthrough performance improvements, measurable skill development, and sustainable competitive advantages in their respective markets.
            </p>

          </div>

          {/* Right Side - Image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden">
              <img 
                src="/Assests/home_page_43.png" 
                alt="Unlock Your Potential" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>

        </div>

      </div>
    </section>

{/* Section 1: Empowering Startups and Enterprises - Improved Design */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-orange-400/70">
      {/* Background stars effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(60)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-orange-200 rounded-full opacity-30 animate-pulse"
            style={{
              width: Math.random() * 3 + 1 + 'px',
              height: Math.random() * 3 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
              animationDelay: Math.random() * 2 + 's',
              animationDuration: Math.random() * 3 + 2 + 's'
            }}
          />
        ))}
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        
        {/* Main Heading */}
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-20 text-center">
          Empowering Startups and Enterprises
        </h2>

        {/* Timeline with Vertical Line */}
        <div className="relative">
          
          {/* Central Vertical Line */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-orange-600/30"></div>

          <div className="space-y-16">
          
            {/* Item 1 - EduTech Expansion (Right Side) */}
            <div className="relative">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                
                {/* Left - Empty Space */}
                <div className="hidden lg:block"></div>
                
                {/* Center Circle */}
                <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 items-center justify-center">
                  <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-lg border-4 border-orange-400/70">
                    1
                  </div>
                </div>

                {/* Right Content */}
                <div className="bg-black/30 rounded-2xl p-8 hover:bg-black/40 transition-all duration-300">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    EduTech Expansion
                  </h3>
                  <p className="text-white text-base leading-relaxed">
                    Educational technology startups leveraging Skill Master's proven go-to-market strategies to expand their geographic footprints and reach new customer segments effectively.
                  </p>
                </div>
                
                {/* Mobile Circle */}
                <div className="lg:hidden flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                    1
                  </div>
                  <div className="flex-1 h-1 bg-orange-600"></div>
                </div>

              </div>
            </div>

            {/* Item 2 - Telecom & Cybersecurity Growth (Left Side) */}
            <div className="relative">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                
                {/* Left Content */}
                <div className="bg-black/30 rounded-2xl p-8 hover:bg-black/40 transition-all duration-300 lg:order-1 order-2">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Telecom & Cybersecurity Growth
                  </h3>
                  <p className="text-white text-base leading-relaxed">
                    Technology firms building robust virtual sales forces and localized market presence through our tailored training and strategic consulting services.
                  </p>
                </div>
                
                {/* Center Circle */}
                <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 items-center justify-center">
                  <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-lg border-4 border-orange-400/70">
                    2
                  </div>
                </div>

                {/* Right - Empty Space */}
                <div className="hidden lg:block lg:order-2"></div>
                
                {/* Mobile Circle */}
                <div className="lg:hidden flex items-center gap-4 mb-4 order-1">
                  <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                    2
                  </div>
                  <div className="flex-1 h-1 bg-orange-600"></div>
                </div>

              </div>
            </div>

            {/* Item 3 - FMCG & Agri Innovation (Right Side) */}
            <div className="relative">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                
                {/* Left - Empty Space */}
                <div className="hidden lg:block"></div>
                
                {/* Center Circle */}
                <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 items-center justify-center">
                  <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-lg border-4 border-orange-400/70">
                    3
                  </div>
                </div>

                {/* Right Content */}
                <div className="bg-black/30 rounded-2xl p-8 hover:bg-black/40 transition-all duration-300">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    FMCG & Agri Innovation
                  </h3>
                  <p className="text-white text-base leading-relaxed">
                    Consumer goods and agricultural startups achieving next-level growth through customized consulting programs that address specific industry challenges and opportunities.
                  </p>
                </div>
                
                {/* Mobile Circle */}
                <div className="lg:hidden flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                    3
                  </div>
                  <div className="flex-1 h-1 bg-orange-600"></div>
                </div>

              </div>
            </div>

          </div>

        </div>

      </div>
    </section>

    {/* Section 2: Human Excellence in Action */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-orange-400/70">
      {/* Background stars effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(60)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-orange-200 rounded-full opacity-30 animate-pulse"
            style={{
              width: Math.random() * 3 + 1 + 'px',
              height: Math.random() * 3 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
              animationDelay: Math.random() * 2 + 's',
              animationDuration: Math.random() * 3 + 2 + 's'
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Main Heading */}
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16 text-center">
          Human Excellence in Action
        </h2>

        {/* Two Column Grid */}
        <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
          
          {/* Left Card - Leadership Transformation */}
          <div className="space-y-6">
            {/* Image */}
            <div className="relative rounded-3xl overflow-hidden">
              <img 
                src="/Assests/home_page_44.png" 
                alt="Leadership Transformation" 
                className="w-full h-80 object-cover"
              />
            </div>
            
            {/* Title */}
            <h3 className="text-2xl font-bold text-black">
              Leadership Transformation
            </h3>
            
            {/* Description */}
            <p className="text-black text-base leading-relaxed">
              Our leadership coaching programs consistently transform capable managers into visionary leaders who inspire teams and drive organizational success through authentic influence and strategic thinking.
            </p>
          </div>

          {/* Right Card - Enhanced Team Communication */}
          <div className="space-y-6">
            {/* Image */}
            <div className="relative rounded-3xl overflow-hidden">
              <img 
                src="/Assests/home_page_45.png" 
                alt="Enhanced Team Communication" 
                className="w-full h-80 object-cover"
              />
            </div>
            
            {/* Title */}
            <h3 className="text-2xl font-bold text-black">
              Enhanced Team Communication
            </h3>
            
            {/* Description */}
            <p className="text-black text-base leading-relaxed">
              Behavioral workshops focusing on communication excellence and soft skills development create more cohesive, productive teams with improved collaboration and conflict resolution capabilities.
            </p>
          </div>

        </div>

      </div>
    </section>

    {/* Section 3: Student & Professional Testimonials */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-orange-400/70">
      {/* Background stars effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(60)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-orange-200 rounded-full opacity-30 animate-pulse"
            style={{
              width: Math.random() * 3 + 1 + 'px',
              height: Math.random() * 3 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
              animationDelay: Math.random() * 2 + 's',
              animationDuration: Math.random() * 3 + 2 + 's'
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Main Heading */}
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16 text-center">
          Student & Professional Testimonials
        </h2>

        {/* Three Column Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          
          {/* Testimonial 1 */}
          <div className="bg-orange-300/60 rounded-2xl p-8 space-y-0 border-2 border-orange-500">
            {/* Quote Icon */}
            <div className="text-orange-600 text-4xl font-serif">"</div>
            
            {/* Testimonial Text */}
            <p className="text-black text-base leading-relaxed italic">
              The flexibility and quality of Skill Master courses perfectly fit my demanding schedule. I could learn at my own pace while maintaining my professional commitments.
            </p>
            <div className="text-orange-600 text-4xl font-serif text-right">"</div>
            
            {/* Author */}
            <div className="pt-4 border-t-2 border-orange-500">
              <p className="font-bold text-black">Abdullah Ahmed</p>
              <p className="text-black text-sm">Marketing Professional</p>
            </div>
          </div>

          {/* Testimonial 2 */}
          <div className="bg-orange-300/60 rounded-2xl p-8 space-y-0 border-2 border-orange-500">
            {/* Quote Icon */}
            <div className="text-orange-600 text-4xl font-serif">"</div>
            
            {/* Testimonial Text */}
            <p className="text-black text-base leading-relaxed italic">
              Interactive tools and engaging content made complex topics accessible and enjoyable. The practical applications were immediately valuable in my work.
            </p>
            <div className="text-orange-600 text-4xl font-serif text-right">"</div>
            
            {/* Author */}
            <div className="pt-4 border-t-2 border-orange-500">
              <p className="font-bold text-black">Tamim Ahmed</p>
              <p className="text-black text-sm">Software Developer</p>
            </div>
          </div>

          {/* Testimonial 3 */}
          <div className="bg-orange-300/60 rounded-2xl p-8 space-y-0 border-2 border-orange-500">
            {/* Quote Icon */}
            <div className="text-orange-600 text-4xl font-serif">"</div>
            
            {/* Testimonial Text */}
            <p className="text-black text-base leading-relaxed italic">
              Personalized coaching sessions helped me identify and overcome limiting beliefs, unlocking potential I didn't know I had. Truly transformational experience.
            </p>
            <div className="text-orange-600 text-4xl font-serif text-right">"</div>
            
            {/* Author */}
            <div className="pt-4 border-t-2 border-orange-500">
              <p className="font-bold text-black">Shariful Islam Rafi</p>
              <p className="text-black text-sm">Project Manager</p>
            </div>
          </div>

        </div>

      </div>
    </section>

        {/* Section 1: Chapter 4 - How Skill Master Works for You */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-black">
      {/* Background stars effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(80)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full opacity-50"
            style={{
              width: Math.random() * 2 + 1 + 'px',
              height: Math.random() * 2 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Main Heading */}
        <h2 className="text-4xl lg:text-5xl font-bold text-white mb-8 text-center">
          Chapter 4: How Skill Master Works for You
        </h2>

        {/* Description Paragraphs */}
        <div className="max-w-5xl mx-auto space-y-6 mb-16 text-center">
          <p className="text-gray-300 text-lg leading-relaxed">
            Understanding how Skill Master integrates into your daily workflow and learning preferences is crucial for maximizing your development investment. Our platform is designed with user experience at its core.
          </p>
          <p className="text-gray-300 text-lg leading-relaxed">
            From initial assessment through certification and beyond, every interaction is optimized for efficiency, engagement, and effective knowledge transfer that translates into real-world performance improvements.
          </p>
        </div>

      </div>
    </section>

    {/* Section 2: Seamless Learning Experience */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-orange-500/70">
      {/* Background stars effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(60)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-orange-200 rounded-full opacity-30 animate-pulse"
            style={{
              width: Math.random() * 3 + 1 + 'px',
              height: Math.random() * 3 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
              animationDelay: Math.random() * 2 + 's',
              animationDuration: Math.random() * 3 + 2 + 's'
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Main Heading */}
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16">
          Seamless Learning Experience
        </h2>

        {/* Three Column Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          
          {/* Column 1 - 24/7 Platform Access */}
          <div className="space-y-4">
            <p className="text-black text-sm font-semibold">01</p>
            <h3 className="text-2xl font-bold text-black">
              24/7 Platform Access
            </h3>
            <p className="text-gray-200 text-base leading-relaxed">
              User-friendly interface accessible anytime, anywhere on any device—desktop, tablet, or mobile—ensuring learning never stops due to location or schedule constraints.
            </p>
          </div>

          {/* Column 2 - Interactive Learning Tools */}
          <div className="space-y-4">
            <p className="text-black text-sm font-semibold">02</p>
            <h3 className="text-2xl font-bold text-black">
              Interactive Learning Tools
            </h3>
            <p className="text-gray-200 text-base leading-relaxed">
              Engaging quizzes, hands-on exercises, and realistic simulations reinforce learning and provide immediate feedback to accelerate skill development and retention.
            </p>
          </div>

          {/* Column 3 - Industry-Recognized Certification */}
          <div className="space-y-4">
            <p className="text-black text-sm font-semibold">03</p>
            <h3 className="text-2xl font-bold text-black">
              Industry-Recognized Certification
            </h3>
            <p className="text-gray-200 text-base leading-relaxed">
              Comprehensive certification upon course completion enhances career credibility and provides tangible evidence of professional development achievements to employers and peers.
            </p>
          </div>

        </div>

      </div>
    </section>

    {/* Section 3: Measurable Outcomes & ROI */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-orange-500/70">
      {/* Background stars effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(60)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-orange-200 rounded-full opacity-30 animate-pulse"
            style={{
              width: Math.random() * 3 + 1 + 'px',
              height: Math.random() * 3 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
              animationDelay: Math.random() * 2 + 's',
              animationDuration: Math.random() * 3 + 2 + 's'
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Main Heading */}
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-black mb-10 sm:mb-16">
          Measurable Outcomes & ROI
        </h2>

        {/* Two Column Grid */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          
          {/* Left Side - Circular Stats */}
          <div className="space-y-12">
            
            {/* Stat 1 - 85% Skill Improvement */}
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="relative w-48 h-48">
                {/* Outer Circle */}
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="96" cy="96" r="80" stroke="#d97706" strokeWidth="12" fill="none" opacity="0.3"/>
                  <circle cx="96" cy="96" r="80" stroke="#ea580c" strokeWidth="12" fill="none" strokeDasharray="502" strokeDashoffset="75" strokeLinecap="round"/>
                </svg>
                {/* Center Text */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-5xl font-bold text-black">85%</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-2">Skill Improvement</h3>
                <p className="text-gray-200 text-sm">Average measured increase in competency levels</p>
              </div>
            </div>

            {/* Stat 2 - 92% ROI Achievement */}
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="relative w-48 h-48">
                {/* Outer Circle */}
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="96" cy="96" r="80" stroke="#d97706" strokeWidth="12" fill="none" opacity="0.3"/>
                  <circle cx="96" cy="96" r="80" stroke="#ea580c" strokeWidth="12" fill="none" strokeDasharray="502" strokeDashoffset="40" strokeLinecap="round"/>
                </svg>
                {/* Center Text */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-5xl font-bold text-black">92%</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-2">ROI Achievement</h3>
                <p className="text-gray-200 text-sm">Organizations report positive return on investment</p>
              </div>
            </div>

            {/* Stat 3 - 78% Integration Success */}
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="relative w-48 h-48">
                {/* Outer Circle */}
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="96" cy="96" r="80" stroke="#d97706" strokeWidth="12" fill="none" opacity="0.3"/>
                  <circle cx="96" cy="96" r="80" stroke="#ea580c" strokeWidth="12" fill="none" strokeDasharray="502" strokeDashoffset="110" strokeLinecap="round"/>
                </svg>
                {/* Center Text */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-5xl font-bold text-black">78%</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-2">Integration Success</h3>
                <p className="text-gray-200 text-sm">Seamless integration with existing systems</p>
              </div>
            </div>

          </div>

          {/* Right Side - Data-Driven Development */}
          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-black">
              Data-Driven Development
            </h3>
            <p className="text-gray-200 text-base leading-relaxed">
              Pre- and post-assessments provide objective measurement of skill improvements, while comprehensive analytics optimize learning and development programs for maximum impact.
            </p>
            <p className="text-gray-200 text-base leading-relaxed">
              Integration capabilities with existing Learning Management Systems and HR platforms ensure seamless deployment and comprehensive tracking across organizational development initiatives.
            </p>
          </div>

        </div>

      </div>
    </section>

    {/* Section 4: Track Your Growth with Real-Time Analytics */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-black">
      {/* Background stars effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(80)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full opacity-50"
            style={{
              width: Math.random() * 2 + 1 + 'px',
              height: Math.random() * 2 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
            }}
          />
        ))}
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        
        {/* Main Heading */}
        <h2 className="text-2xl sm:text-4xl lg:text-6xl font-bold text-white mb-6 sm:mb-8">
          Track Your Growth with Real-Time Analytics
        </h2>

        {/* Description */}
        <p className="text-gray-300 text-lg lg:text-xl leading-relaxed max-w-5xl">
          Visualize your development journey with comprehensive dashboards that showcase skill progression, achievement milestones, and personalized recommendations for continued growth and excellence.
        </p>

      </div>
    </section>

    {/* Section 1: Chapter 5 - Get Started with Skill Master Today */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-20 sm:py-32 overflow-hidden min-h-screen flex items-center">
      {/* Background Image */}
      <div className="absolute inset-0 overflow-hidden">
        <img 
          src="/Assests/home_page_46.png" 
          alt="Chapter 5 Background" 
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/50"></div>

      <div className="max-w-6xl mx-auto relative z-10 w-full">
        
        {/* Content Card */}
        <div className="bg-black/70 rounded-3xl p-6 sm:p-12 lg:p-16 border border-gray-600">
          
          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-6xl font-bold text-white mb-6 sm:mb-8 leading-tight">
            Get Started with Skill Master Today
          </h2>

          {/* First Paragraph */}
          <p className="text-gray-300 text-lg lg:text-xl leading-relaxed mb-6">
            Your transformation begins with a single decision to invest in your future. Skill Master provides the roadmap, tools, and support system needed to achieve your personal and professional aspirations.
          </p>

          {/* Second Paragraph */}
          <p className="text-gray-300 text-lg lg:text-xl leading-relaxed">
            Join a community of forward-thinking individuals and organizations who have chosen excellence as their standard. The time for incremental improvement has passed—it's time for exponential growth.
          </p>

        </div>

      </div>
    </section>

    {/* Section 2: Your Path to Accelerated Excellence Begins Now */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-orange-500/80">
      {/* Background stars effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(60)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-orange-200 rounded-full opacity-30 animate-pulse"
            style={{
              width: Math.random() * 3 + 1 + 'px',
              height: Math.random() * 3 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
              animationDelay: Math.random() * 2 + 's',
              animationDuration: Math.random() * 3 + 2 + 's'
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Main Heading */}
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16 text-center">
          Your Path to Accelerated Excellence Begins Now
        </h2>

        {/* Three Steps Grid */}
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-12 mb-8 sm:mb-12">
          
          {/* Step 1 - Free Consultation */}
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-300 rounded-full flex items-center justify-center text-black font-bold text-xl">
                1
              </div>
              <h3 className="text-2xl font-bold text-black">
                Free Consultation
              </h3>
            </div>
            <p className="text-black text-base leading-relaxed">
              Personalized assessment and tailored recommendations to design your optimal upskilling journey based on your unique goals and current capabilities.
            </p>
          </div>

          {/* Step 2 - Join Thousands of Success Stories */}
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-300 rounded-full flex items-center justify-center text-black font-bold text-xl">
                2
              </div>
              <h3 className="text-2xl font-bold text-black">
                Join Thousands of Success Stories
              </h3>
            </div>
            <p className="text-black text-base leading-relaxed">
              Become part of a growing community of professionals and organizations who have transformed their futures through strategic skill development and continuous learning.
            </p>
          </div>

          {/* Step 3 - Start Your Growth Journey */}
            <div className="space-y-6">
            <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-300 rounded-full flex items-center justify-center text-black font-bold text-xl">
                3
                </div>
                <h3 className="text-2xl font-bold text-black">
                Start Your Growth Journey
                </h3>
            </div>

            <p className="text-black text-base leading-relaxed">
                Visit{" "}
                <a
                href="https://skillmaster.net.in"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white font-semibold hover:underline hover:text-white transition-colors"
                >
                skillmaster.net.in
                </a>{" "}
                to explore our comprehensive course catalog and begin your transformation
                today with confidence and expert support.
            </p>
            </div>

        </div>

        {/* CTA Buttons */}
        <div className="flex flex-wrap justify-center gap-6">
          <button
            onClick={() => navigate('/reffrence?productId=4')}
            className="px-8 py-4 bg-white text-orange-500 border-2 border-orange-500 rounded-lg font-medium text-lg hover:bg-orange-800 hover:text-white hover:border-white transition">
            Explore Courses Now
          </button>
          <button
            onClick={() => navigate('/reffrence?productId=4')}
            className="bg-orange-800 px-8 py-4 border-2 border-white text-white rounded-lg font-medium text-lg hover:bg-white hover:text-orange-500 hover:border-orange-800 transition">
            Schedule Free Consultation
          </button>
        </div>

      </div>
    </section>
    {/* Join the Revolution Section - SkillMaster */}
 <section className="relative px-6 sm:px-10 lg:px-24 py-20 bg-black">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">Master New Skills with SkillMaster</h2>
          <p className="text-gray-400 text-lg mb-12 max-w-3xl">
            Learn from expert-crafted courses and books designed to boost your career. Visit{" "}
            <a href="https://skillmaster.net.in/" target="_blank" rel="noopener noreferrer" className="text-orange-500 font-semibold hover:underline">
              skillmaster.net.in
            </a>{" "}
            to access high-quality learning resources.
          </p>
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-white rounded-2xl p-8 hover:border-orange-500 transition">
              <h3 className="text-2xl font-bold text-white mb-4">Quality Courses</h3>
              <p className="text-gray-300 leading-relaxed">Choose from a wide range of practical courses to grow your skills.</p>
            </div>
            <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-white rounded-2xl p-8 hover:border-orange-500 transition">
              <h3 className="text-2xl font-bold text-white mb-4">Expert Books</h3>
              <p className="text-gray-300 leading-relaxed">Get exclusive books written by industry professionals and educators.</p>
            </div>
          </div>
          {/* Buttons */}
          <div className="flex flex-wrap gap-4">
            {hasRefAccess && (
            <button
              onClick={() => navigate('/reffrence?productId=4')}
              className="px-8 py-4 bg-orange-500 text-white text-lg font-semibold rounded-lg hover:bg-orange-600 transition flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              Reference
            </button>
            )}
            <button
              onClick={() => navigate('/reffrence?productId=4')}
              className="px-8 py-4 bg-orange-500 text-white text-lg font-semibold rounded-lg hover:bg-orange-600 transition">
              Browse Courses
            </button>
            <button
              onClick={() => navigate('/reffrence?productId=4')}
              className="px-8 py-4 border-2 border-white text-white text-lg font-semibold rounded-lg hover:bg-white hover:border-orange-500 hover:text-orange-500 transition">
              Explore Books
            </button>
          </div>
        </div>
      </section>


    </div>
  );
}

export default SkillMaster;